import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageCircle, Facebook, Twitter, Mail, Link as LinkIcon, QrCode, Copy, Check, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const FALLBACK_DOMAIN = 'https://www.christkingdomglobalmarketplace.com';

export default function ShareStoreToolkit({ vendor }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  if (!vendor || !vendor.store_slug) {
    return null;
  }

  // Always share the production URL — even when the vendor is browsing on a preview.
  const storeUrl = `${FALLBACK_DOMAIN}/store/${vendor.store_slug}`;
  const businessName = vendor.business_name || 'My Store';

  const shareMessage = `Hi! I just opened my store on Christ Kingdom Global Marketplace — a faith-driven marketplace for Christians worldwide. Would love your support! 🙏\n\nVisit ${businessName}: ${storeUrl}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      toast.success('Storefront link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openEmail = () => {
    const subject = encodeURIComponent(`Check out my store: ${businessName}`);
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(storeUrl)}`;

  return (
    <Card data-testid="share-store-toolkit" className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-emerald-900">
          <LinkIcon className="h-5 w-5 text-emerald-600" />
          Share Your Store
        </CardTitle>
        <CardDescription className="text-emerald-800/80">
          The fastest way to your first sale: send this link to your contacts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Shareable URL */}
        <div className="flex gap-2 flex-wrap">
          <Input
            readOnly
            value={storeUrl}
            className="font-mono text-xs flex-1 min-w-[220px]"
            data-testid="store-url-input"
            onFocus={(e) => e.target.select()}
          />
          <Button
            type="button"
            onClick={copyLink}
            variant="outline"
            className="gap-2"
            data-testid="copy-store-url"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            type="button"
            onClick={() => window.open(storeUrl, '_blank', 'noopener,noreferrer')}
            variant="outline"
            className="gap-2"
            data-testid="open-store-url"
          >
            <ExternalLink className="h-4 w-4" />
            Visit
          </Button>
        </div>

        {/* Share buttons grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button
            type="button"
            onClick={openWhatsApp}
            className="bg-[#25D366] hover:bg-[#1ebe5b] text-white gap-2"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </Button>
          <Button
            type="button"
            onClick={openFacebook}
            className="bg-[#1877F2] hover:bg-[#0c5fc4] text-white gap-2"
            data-testid="share-facebook"
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </Button>
          <Button
            type="button"
            onClick={openTwitter}
            className="bg-black hover:bg-gray-800 text-white gap-2"
            data-testid="share-twitter"
          >
            <Twitter className="h-4 w-4" />
            Twitter / X
          </Button>
          <Button
            type="button"
            onClick={openEmail}
            variant="outline"
            className="gap-2"
            data-testid="share-email"
          >
            <Mail className="h-4 w-4" />
            Email
          </Button>
        </div>

        {/* QR Code section */}
        <div className="border border-emerald-200 bg-white rounded-md p-3">
          <button
            type="button"
            onClick={() => setShowQR((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-emerald-900 hover:text-emerald-700"
            data-testid="toggle-qr"
          >
            <QrCode className="h-4 w-4 text-emerald-600" />
            {showQR ? 'Hide QR code' : 'Show QR code (great for flyers / church bulletins)'}
          </button>
          {showQR && (
            <div className="mt-3 flex items-start gap-4 flex-wrap" data-testid="qr-code-block">
              <img
                src={qrUrl}
                alt={`QR code for ${businessName}`}
                width={180}
                height={180}
                className="rounded-md border border-emerald-100"
              />
              <div className="text-xs text-emerald-800 max-w-xs space-y-2 leading-relaxed">
                <p>
                  <strong>How to use:</strong> Right-click the QR → Save image.
                  Print on flyers, business cards, or share on Instagram stories.
                </p>
                <p>
                  Anyone who scans this with their phone camera lands directly on
                  your store. Perfect for in-person events, church bulletins, or
                  product packaging.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pre-written message for vendors to copy */}
        <details className="border border-emerald-200 rounded-md p-3 bg-white">
          <summary className="cursor-pointer text-sm font-medium text-emerald-900">
            📝 Sample message you can copy &amp; send
          </summary>
          <div className="mt-2">
            <textarea
              readOnly
              rows={4}
              className="w-full text-xs p-2 border border-emerald-200 rounded font-mono bg-emerald-50/30"
              value={shareMessage}
              onFocus={(e) => e.target.select()}
              data-testid="share-message-template"
            />
            <p className="text-xs text-emerald-700 mt-1">
              Tip: send this to 10 contacts today — most vendors get their first 2-3 sales within 48 hours of sharing.
            </p>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}
